import React, { useState, useEffect } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { jwtDecode } from "jwt-decode";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";

import {
  getCustomerById,
  getContactsByCustomerId,
  addContact,
  updateContact,
  deleteContact,
  getActivitiesByCustomerId,
  addActivity,
  updateActivity,
  deleteActivity,
  getMarcasClient,
} from "../../api/Customer/customerApi.js";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import {
  getProyectosByCustomerId,
  addProyecto,
  updateProyecto,
  deleteProyecto,
} from "../../api/Proyectos/proyectoApi";
import { incrementarContador } from "../../api/Customer/contadoresApi";
import { getUserById } from "../../api/User/userApi";

import { getEstadosProyecto } from "../../api/Estados/estadosApi";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CustomerDetails from "./CustomerDetails";
import Contacts from "./Contacts";
import Activities from "./Activities";
import Projects from "./Projects";
import CustomerDocs from "./CustomerDocs.js";

import ProjectDrawer from "./ProjectDrawer";
import { useTranslation } from "react-i18next";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { pageSectionTitleSx } from "../../styles/pageModernStyles";

const getTokenUser = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return {};
    return jwtDecode(token) || {};
  } catch {
    return {};
  }
};

const normalizeMultiValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (value == null) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => String(item).trim())
            .filter(Boolean);
        }
      } catch {
        // Fall back to comma-separated parsing below
      }
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [String(value).trim()].filter(Boolean);
};

const buildCustomerFormState = (data = {}) => ({
  ...data,
  marketingOk: data.marketingOk ? "Si" : "No",
  observacionesOk: data.observacionesOk ? "Si" : "No",
  createdAt: data.fecha_creacion
    ? new Date(data.fecha_creacion).toLocaleDateString("es-ES")
    : "",
  updatedAt: data.fecha_actualizacion
    ? new Date(data.fecha_actualizacion).toLocaleDateString("es-ES")
    : "",
  procedencia: normalizeMultiValue(data.procedencia),
  tipo_paqueteria: normalizeMultiValue(data.tipo_paqueteria),
  tipo_envios: normalizeMultiValue(data.tipo_envios),
  transportista_actual: normalizeMultiValue(data.transportista_actual),
  fuente_lead: normalizeMultiValue(data.fuente_lead),
  marcas: normalizeMultiValue(data.marcas),
});

const normalizeList = (value) => (Array.isArray(value) ? value : []);

class CustomerPanelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error(`Customer panel crashed: ${this.props.panelName}`, error);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resetKey !== this.props.resetKey &&
      this.state.hasError
    ) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: "calc(100vh - 35px)",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            p: 2,
          }}
        >
          <Typography sx={{ fontWeight: 900 }}>
            {this.props.panelName}
          </Typography>
          <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: "0.9rem" }}>
            Este panel tiene datos no validos para este cliente.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

const CustomerForm = ({ customerId, onSubmit, onClose }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobilePanel, setMobilePanel] = useState(0);
  const [customer, setCustomer] = useState({
    name: "",
    procedencia: [],
    tipo_paqueteria: [],
    contact: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    phone: "",
    email: "",
    marketingOk: "",
    observacionesOk: "",
    nEnvios: "",
    abc: "",
    estado: "",
    comentarioPerdida: "",
    tipo_cliente: "",
    industria: "",
    categoría: "",
    tipo_envios: [],
    transportista_actual: [],
    fuente_lead: [],
    ecommerce_plataformas: "",
    ingresos_estimados: "",
    observaciones: "",
    tarifa_sq: "",
    createdAt: "",
    updatedAt: "",
    responsableVentas: "",
    Web: "",
    marcas: [],
  });

  const [marcas, setMarcas] = useState([]);

  const [proyectos, setProyectos] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [newProyecto, setNewProyecto] = useState({
    subject: "",
    proyectType: "",
    status: "",
    notes: "",
    previsionVenta: "",
    previsionPrendas: "",
    ventaFinal: "",
    prendasFinal: "",
    marcaId: "",
  });
  const [editProyectoId, setEditProyectoId] = useState(null);
  const [temporadas, setTemporadas] = useState([]);

  const [openProyectoErrorDialog, setOpenProyectoErrorDialog] = useState(false);
  const handleCloseProyectoErrorDialog = () =>
    setOpenProyectoErrorDialog(false);

  const setDrawerOpen = (isOpen) => setShowDrawer(isOpen);

  const tokenUser = getTokenUser();
  const userId = tokenUser.userId;
  const isDeletionAllowed = ![10, 11].includes(Number(userId));

  // Permisos para usuario dar alta clientes, etc
  const [userPermissions, setUserPermissions] = useState({});
  const [saving, setSaving] = useState(false);

  const [estados, setEstados] = useState([]);

  useEffect(() => {
    if (!userId) return;
    getUserById(userId)
      .then((data) => setUserPermissions(data || {}))
      .catch((err) => console.error("Error loading permissions:", err));
  }, [userId]);
  // Permisos para usuario dar alta clientes, etc

  const canSaveCustomer = Boolean(
    userPermissions.isAdmin ||
      tokenUser.isAdmin ||
      userPermissions.Permiso_ClientsUpdate ||
      (!customerId && userPermissions.Permiso_Clients)
  );

  useEffect(() => {
    getMarcasClient()
      .then((data) => {
        setMarcas(normalizeList(data));
      })
      .catch((error) => console.error("Error cargando marcas:", error));

    if (customerId) {
      getCustomerById(customerId).then((data) => {
        setCustomer(buildCustomerFormState(data));
      });

      getContactsByCustomerId(customerId)
        .then((data) => setContacts(normalizeList(data)))
        .catch((error) => console.error("Error cargando contactos:", error));

      getActivitiesByCustomerId(customerId)
        .then((data) => setActivities(normalizeList(data)))
        .catch((error) => console.error("Error cargando actividades:", error));

      getProyectosByCustomerId(customerId)
        .then((data) => setProyectos(normalizeList(data)))
        .catch((error) => console.error("Error cargando Proyectos:", error));

      getTemporadas()
        .then((data) => setTemporadas(normalizeList(data)))
        .catch((error) => console.error("Error cargando Temporadas:", error));
    }
  }, [customerId]);

  useEffect(() => {
    getEstadosProyecto()
      .then((data) => setEstados(normalizeList(data)))
      .catch((err) => console.error("Error cargando estados:", err));
  }, []);

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      [name]: value,
    }));
  };

  const handleMultiSelectChange = (e, name) => {
    const { value } = e.target;
    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      [name]: Array.isArray(value) ? value : [],
    }));
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    if (!canSaveCustomer || saving) return;

    if (!String(customer.name || "").trim()) {
      window.alert(
        t("Clientes.nameRequired", {
          defaultValue: "El nombre de la empresa es obligatorio.",
        })
      );
      return;
    }

    const updatedCustomer = {
      ...customer,
      name: String(customer.name).trim(),
      marketingOk: customer.marketingOk === "Si",
      observacionesOk: customer.observacionesOk === "Si",
      marcas: Array.isArray(customer.marcas) ? customer.marcas.join(", ") : "",
    };

    setSaving(true);
    try {
      await onSubmit(updatedCustomer);
      if (customerId) {
        const refreshedCustomer = await getCustomerById(customerId);
        setCustomer(buildCustomerFormState(refreshedCustomer));
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error al guardar el cliente:", error);
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      window.alert(
        t("Clientes.saveError", {
          defaultValue: "No se pudo guardar el cliente. {{detail}}",
          detail: apiMessage || "",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  /* contacts */
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    position: "",
  });
  const [editContactId, setEditContactId] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async () => {
    try {
      if (editContactId) {
        await updateContact(editContactId, newContact);
      } else {
        await addContact(customerId, newContact);
      }
      const updated = await getContactsByCustomerId(customerId);
      setContacts(normalizeList(updated));
      setNewContact({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        position: "",
      });
      setEditContactId(null);
      setShowContactForm(false);
    } catch (err) {
      console.error("Error saving contact:", err);
    }
  };

  const handleEditContact = (contact) => {
    setNewContact(contact);
    setEditContactId(contact.id);
    setShowContactForm(true);
  };

  const handleAddNewContact = () => {
    setNewContact({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      position: "",
    });
    setEditContactId(null);
    setShowContactForm(!showContactForm);
  };

  const [confirmMessage, setConfirmMessage] = useState("");
  const [deleteAction, setDeleteAction] = useState(() => {});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openConfirmDeleteProyectoDialog, setOpenConfirmDeleteProyectoDialog] =
    useState(false);
  const [selectedProyectoId, setSelectedProyectoId] = useState(null);

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleDeleteContact = (contactId) => {
    if (!isDeletionAllowed) {
      alert("You are not allowed to delete contacts.");
      return;
    }
    setConfirmMessage("Are you sure you want to delete this contact?");
    setDeleteAction(() => async () => {
      try {
        await deleteContact(contactId);
        const updated = await getContactsByCustomerId(customerId);
        setContacts(normalizeList(updated));
        handleCloseConfirmDialog();
      } catch (err) {
        console.error("Error deleting contact:", err);
      }
    });
    setOpenConfirmDialog(true);
  };
  /* contacts */

  // Activities
  /* Activities */
  const [activities, setActivities] = useState([]);
  const [newActivity, setNewActivity] = useState({
    subject: "",
    activityType: "",
    status: "",
    notes: "",
    activityDate: "",
    gMeetId: "",
  });
  const [editActivityId, setEditActivityId] = useState(null);
  const [showActivityForm, setShowActivityForm] = useState(false);

  const handleActivityChange = (e) => {
    const { name, value } = e.target;
    setNewActivity((prev) => ({ ...prev, [name]: value }));
  };

  const handleActivitySubmit = async () => {
    try {
      if (editActivityId) {
        await updateActivity(editActivityId, newActivity);
      } else {
        await addActivity(customerId, newActivity);
      }
      const updated = await getActivitiesByCustomerId(customerId);
      setActivities(normalizeList(updated));
      setNewActivity({
        subject: "",
        activityType: "",
        status: "",
        notes: "",
        activityDate: "",
        gMeetId: "",
      });
      setEditActivityId(null);
      setShowActivityForm(false);
    } catch (err) {
      console.error("Error saving activity:", err);
    }
  };

  const handleEditActivity = (activity) => {
    setNewActivity(activity);
    setEditActivityId(activity.id);
    setShowActivityForm(true);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!isDeletionAllowed) {
      alert("You are not allowed to delete activities.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this activity?")) {
      try {
        await deleteActivity(activityId);
        const updated = await getActivitiesByCustomerId(customerId);
        setActivities(normalizeList(updated));
      } catch (err) {
        console.error("Error deleting activity:", err);
      }
    }
  };

  const handleAddNewActivity = () => {
    setNewActivity({
      subject: "",
      activityType: "",
      status: "",
      notes: "",
      activityDate: "",
      gMeetId: "",
    });
    setEditActivityId(null);
    setShowActivityForm(!showActivityForm);
  };
  /* Activities */

  // Projects

  useEffect(() => {
    if (customerId) {
      getProyectosByCustomerId(customerId)
        .then(setProyectos)
        .catch((err) => console.error("Error loading proyectos:", err));
    }
  }, [customerId]);

  const handleEditProyecto = (proyecto) => {
    if (proyecto) {
      setNewProyecto({
        ...proyecto,
        previsionVenta: (proyecto.previsionVenta || "")
          .toString()
          .replace(/\./g, ""),
        previsionPrendas: (proyecto.previsionPrendas || "")
          .toString()
          .replace(/\./g, ""),
        ventaFinal: (proyecto.ventaFinal || "").toString().replace(/\./g, ""),
        prendasFinal: (proyecto.prendasFinal || "")
          .toString()
          .replace(/\./g, ""),
      });
      setEditProyectoId(proyecto.id);
    } else {
      setNewProyecto({
        subject: "",
        proyectType: "",
        status: "",
        notes: "",
        previsionVenta: "",
        previsionPrendas: "",
        ventaFinal: "",
        prendasFinal: "",
        marcaId: "",
      });
      setEditProyectoId(null);
    }
    setShowDrawer(true);
  };

  const handleDeleteProyecto = async (proyectoId) => {
    setSelectedProyectoId(proyectoId);
    setOpenConfirmDeleteProyectoDialog(true);
  };

  const confirmarEliminacionProyecto = async () => {
    try {
      await deleteProyecto(selectedProyectoId);
      const updatedProyectos = await getProyectosByCustomerId(customerId);
      setProyectos(normalizeList(updatedProyectos));
    } catch (error) {
      if (error?.response?.data?.code === "FK_PRESUPUESTOS_PROYECTOS") {
        setOpenProyectoErrorDialog(true);
      } else {
        console.error("Error deleting project:", error);
      }
    } finally {
      setOpenConfirmDeleteProyectoDialog(false);
      setSelectedProyectoId(null);
    }
  };

  const handleProyectoChange = (e) => {
    const { name, value } = e.target;

    const cleanedValue =
      typeof value === "string" &&
      (name.toLowerCase().includes("venta") ||
        name.toLowerCase().includes("prendas"))
        ? value.replace(/\./g, "")
        : value;

    setNewProyecto((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));
  };

  const formatNumberWithDots = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const handleProyectoSubmit = async () => {
    try {
      let proyectoToSave = {
        ...newProyecto,
        previsionVenta: parseFloat(newProyecto.previsionVenta) || 0,
        previsionPrendas: parseInt(newProyecto.previsionPrendas, 10) || 0,
        ventaFinal: parseFloat(newProyecto.ventaFinal) || 0,
        prendasFinal: parseInt(newProyecto.prendasFinal, 10) || 0,
        marcaId: newProyecto.marcaId ? parseInt(newProyecto.marcaId, 10) : null,
        customerId,
      };

      // Si es un nuevo proyecto y no se ha escrito el subject, lo generamos automáticamente
      if (!editProyectoId && !newProyecto.subject?.trim()) {
        const contador = await incrementarContador(newProyecto.marcaId);
        const marcaName =
          marcas
            .find((m) => m.ID_MARCA === newProyecto.marcaId)
            ?.MARCA.slice(0, 2) || "Un";
        const temporada = newProyecto.proyectType || "Season";

        proyectoToSave.subject = `${marcaName.trim()} - ${temporada.trim()} - ${contador}`;
      }

      if (editProyectoId) {
        await updateProyecto(editProyectoId, proyectoToSave);
      } else {
        await addProyecto(customerId, proyectoToSave);
      }

      const updated = await getProyectosByCustomerId(customerId);
      setProyectos(normalizeList(updated));

      setNewProyecto({
        subject: "",
        proyectType: "",
        status: "",
        notes: "",
        previsionVenta: "",
        previsionPrendas: "",
        ventaFinal: "",
        prendasFinal: "",
        marcaId: "",
      });
      setEditProyectoId(null);
      setShowDrawer(false);
    } catch (err) {
      console.error("Error saving project:", err);
    }
  };

  // Projects

  const COLUMN_WIDTH = "100%";
  const dialogPaperSx = {
    borderRadius: 4,
    border: "1px solid",
    borderColor: alpha(theme.palette.primary.main, 0.1),
    background: `linear-gradient(180deg, ${alpha(
      theme.palette.common.white,
      0.94,
    )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
    boxShadow: `0 24px 60px ${alpha(theme.palette.common.black, 0.16)}`,
    backdropFilter: "blur(14px)",
  };

  const detailsPanel = (
    <CustomerDetails
      customer={customer}
      marcas={marcas}
      handleCustomerChange={handleCustomerChange}
      handleMultiSelectChange={handleMultiSelectChange}
      customerId={customerId}
      canUpdate={canSaveCustomer}
      saving={saving}
      COLUMN_WIDTH={COLUMN_WIDTH}
    />
  );

  const contactsPanel = (
    <Contacts
      contacts={contacts}
      newContact={newContact}
      setNewContact={setNewContact}
      editContactId={editContactId}
      setEditContactId={setEditContactId}
      showContactForm={showContactForm}
      setShowContactForm={setShowContactForm}
      handleContactChange={handleContactChange}
      handleContactSubmit={handleContactSubmit}
      handleEditContact={handleEditContact}
      handleDeleteContact={handleDeleteContact}
      handleAddNewContact={handleAddNewContact}
      isDeletionAllowed={isDeletionAllowed}
      canAdd={userPermissions.Permiso_ClientsAddContact}
      COLUMN_WIDTH={COLUMN_WIDTH}
    />
  );

  const docsPanel = <CustomerDocs customerId={customerId} COLUMN_WIDTH={COLUMN_WIDTH} />;

  const activitiesPanel = (
    <Activities
      activities={activities}
      newActivity={newActivity}
      setNewActivity={setNewActivity}
      editActivityId={editActivityId}
      setEditActivityId={setEditActivityId}
      showActivityForm={showActivityForm}
      setShowActivityForm={setShowActivityForm}
      handleActivityChange={handleActivityChange}
      handleActivitySubmit={handleActivitySubmit}
      handleEditActivity={handleEditActivity}
      handleDeleteActivity={handleDeleteActivity}
      handleAddNewActivity={handleAddNewActivity}
      isDeletionAllowed={isDeletionAllowed}
      canAdd={userPermissions.Permiso_ClientsAddActivity}
      COLUMN_WIDTH={COLUMN_WIDTH}
    />
  );

  const projectsPanel = (
    <Projects
      proyectos={proyectos}
      marcas={marcas}
      temporadas={temporadas}
      estados={estados}
      editProyectoId={editProyectoId}
      isDeletionAllowed={isDeletionAllowed}
      handleEditProyecto={handleEditProyecto}
      handleDeleteProyecto={handleDeleteProyecto}
      setDrawerOpen={setDrawerOpen}
      canAdd={userPermissions.Permiso_ClientsAddProject}
      COLUMN_WIDTH={COLUMN_WIDTH}
    />
  );

  const mobilePanels = [
    {
      label: t("Clientes.Client"),
      shortLabel: t("Clientes.Client"),
      icon: PersonOutlineOutlinedIcon,
      node: detailsPanel,
      needsId: false,
    },
    {
      label: t("Clientes.Contact"),
      shortLabel: t("Clientes.Contact"),
      icon: ContactsOutlinedIcon,
      node: contactsPanel,
      needsId: true,
    },
    {
      label: "Docs",
      shortLabel: "Docs",
      icon: DescriptionOutlinedIcon,
      node: docsPanel,
      needsId: true,
    },
    {
      label: t("Activities.Activities"),
      shortLabel: t("Activities.Activities"),
      icon: EventNoteOutlinedIcon,
      node: activitiesPanel,
      needsId: true,
    },
    {
      label: t("Projects.Projects"),
      shortLabel: t("Projects.Projects"),
      icon: WorkOutlineOutlinedIcon,
      node: projectsPanel,
      needsId: true,
    },
  ];

  const tabAccent = theme.palette.primary.main;

  const renderPanel = (panel, index) => {
    if (isCompactLayout && mobilePanel !== index) return null;
    if (panel.needsId && !customerId) {
      return (
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("Clientes.saveClientFirst", {
              defaultValue: "Guarda el cliente primero para acceder a esta sección.",
            })}
          </Typography>
        </Box>
      );
    }
    return panel.node;
  };

  return (
    <Box
      component="form"
      onSubmit={handleCustomerSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "100dvh", lg: "auto" },
        bgcolor: alpha(theme.palette.background.default, 0.35),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.25, md: 1.75 },
          py: { xs: 1.1, md: 1.25 },
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.7),
          background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 72%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...pageSectionTitleSx, fontSize: "0.62rem" }}>
              CRM · Clientes
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              {customerId
                ? customer.name || t("Clientes.Client")
                : t("Clientes.addCustomerTooltip", { defaultValue: "Nuevo cliente" })}
            </Typography>
            {customerId ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {customer.city || customer.country
                  ? [customer.city, customer.country].filter(Boolean).join(" · ")
                  : t("Clientes.clickRowHint", {
                      defaultValue: "Edita los datos y las secciones relacionadas.",
                    })}
              </Typography>
            ) : null}
          </Box>
          {onClose ? (
            <IconButton
              onClick={onClose}
              aria-label="close"
              size="small"
              sx={{
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.8),
                bgcolor: alpha(theme.palette.background.paper, 0.9),
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: isCompactLayout ? "flex" : "grid",
          flexDirection: isCompactLayout ? "column" : undefined,
          gridTemplateColumns: isCompactLayout
            ? undefined
            : "repeat(5, minmax(0, 1fr))",
          alignItems: "stretch",
          gap: 0,
          width: "100%",
          overflowX: isCompactLayout ? "visible" : "hidden",
          "& > *": {
            minWidth: 0,
          },
        }}
      >
      {isCompactLayout ? (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 12,
            mx: { xs: -0.3, sm: 0 },
            mb: 1,
            pt: 0.25,
            pb: 0.75,
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            backdropFilter: "blur(10px)",
            borderBottom: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.8),
          }}
        >
          <Box
            sx={{
              p: 0.5,
              borderRadius: 2.5,
              bgcolor: alpha(tabAccent, 0.07),
              border: "1px solid",
              borderColor: alpha(tabAccent, 0.14),
            }}
          >
            <Tabs
              value={mobilePanel}
              onChange={(_, value) => setMobilePanel(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{ sx: { display: "none" } }}
              sx={{
                minHeight: 42,
                "& .MuiTabs-flexContainer": { gap: 0.5 },
                "& .MuiTabs-scrollButtons": {
                  width: 28,
                  borderRadius: 1.5,
                  color: "primary.main",
                },
                "& .MuiTab-root": {
                  minHeight: 36,
                  minWidth: "auto",
                  py: 0.65,
                  px: 1.35,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: "0.76rem",
                  textTransform: "none",
                  color: "text.secondary",
                  opacity: 1,
                  transition: "background-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease",
                  border: "1px solid transparent",
                  "& .MuiTab-iconWrapper": {
                    marginBottom: 0,
                    marginRight: 6,
                  },
                  "&.Mui-selected": {
                    color: "primary.dark",
                    bgcolor: theme.palette.background.paper,
                    borderColor: alpha(tabAccent, 0.28),
                    boxShadow: `0 4px 14px ${alpha(tabAccent, 0.18)}`,
                  },
                  "&.Mui-disabled": {
                    opacity: 0.42,
                  },
                },
              }}
            >
              {mobilePanels.map((panel, index) => {
                const Icon = panel.icon;
                return (
                  <Tab
                    key={panel.label}
                    icon={<Icon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                    label={panel.shortLabel}
                    disabled={panel.needsId && !customerId}
                  />
                );
              })}
            </Tabs>
          </Box>
        </Box>
      ) : null}

      {mobilePanels.map((panel, index) => (
        <CustomerPanelErrorBoundary
          key={panel.label}
          panelName={panel.label}
          resetKey={`${customerId}-${panel.label}`}
        >
          {renderPanel(panel, index)}
        </CustomerPanelErrorBoundary>
      ))}
      </Box>

      <ProjectDrawer
        open={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setEditProyectoId(null); // ← Esto es lo que resetea el botón a "Add Project"
        }}
        editProyectoId={editProyectoId}
        newProyecto={newProyecto}
        marcas={marcas}
        temporadas={temporadas}
        handleProyectoChange={handleProyectoChange}
        handleProyectoSubmit={handleProyectoSubmit}
        formatNumberWithDots={formatNumberWithDots}
        setNewProyecto={setNewProyecto}
        estados={estados}
      />

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle id="confirm-dialog-title">Confirm deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={deleteAction} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* NO SE PUEDE ELIMINAR PROYECTO SI TIENE PRESUPUESTO */}
      <Dialog
        open={openProyectoErrorDialog}
        onClose={handleCloseProyectoErrorDialog}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>No se puede eliminar el proyecto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Este proyecto tiene presupuestos asociados y no puede ser eliminado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProyectoErrorDialog} autoFocus>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      {/* ELIMINAR SI NO TIENE PRESUPUESTO */}
      <Dialog
        open={openConfirmDeleteProyectoDialog}
        onClose={() => setOpenConfirmDeleteProyectoDialog(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>¿Confirmar eliminación?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar este proyecto? Esta acción no
            se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDeleteProyectoDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={confirmarEliminacionProyecto} autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerForm;
